import { useMemo, useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import useRBAC from "../hooks/useRBAC";

import { Upload, Search, FileText,  Inbox } from "lucide-react";//FileSearch,

/* ---------------- MODULE CONFIG ---------------- */
const ALL_MODULES = [
  {
    key: "UPLOAD_DOCUMENT",
    title: "Upload Document",
    desc: "Upload a new policy or claim document",
    path: "/upload",
    resources: ["UPLOAD_DOCUMENT"],
    icon: Upload,
  },

  {
    key: "SEARCH_DOCUMENTS",
    title: "Search Documents",
    desc: "Find documents across the portal",
    path: "/search",
    resources: ["SEARCH_DOC"],
    icon: Search,
  },

  {
    key: "ARCHIVAL_POLICY_SEARCH",
    title: "Archival Policy Search",
    desc: "Look up archived policy records",
    path: "/policy-search",
    resources: ["ARCHIVAL_POLICY_SEARCH"],
    icon: FileText,
  },

  // {
  //   key: "ARCHIVAL_CLAIM_SEARCH",
  //   title: "Archival Claim Search",
  //   desc: "Look up archived claim records",
  //   path: "/claim-search",
  //   resources: ["ARCHIVAL_CLAIM_SEARCH"],
  //   icon: FileSearch,
  // },

  {
    key: "DOCUMENT_HUB",
    title: "Inbox Review",
    desc: "Review new inbox documents",
    path: "/document-hub",
    resources: ["DOC_INDEX_HUB"],
    icon: Inbox,
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { permissions, loading } = useRBAC();

  const [role, setRole] = useState([]);
  const [roleError, setRoleError] = useState(false);

  /* ---------------- LOAD ROLE ---------------- */
  const loadRole = useCallback(
    async (signal) => {
      /*
      Prevent duplicate role calls
    */

      try {
        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/user/my-roles",
          null,
          {
            signal,
          },
        );

        /*
        Ignore cancelled requests
      */

        if (signal?.aborted) {
          return;
        }

        console.log("ROLE API:", res);

        const roles = res?.data?.result?.roles || [];

        if (roles.length > 0) {
          setRole(roles);

          setRoleError(false);
        } else {
          setRole([]);

          setRoleError(true);
        }
      } catch (err) {
        /*
        Ignore cancellation
      */

        if (
          err?.name === "AbortError" ||
          err?.message === "Request cancelled"
        ) {
          return;
        }

        console.error("Role load failed", err);

        setRole([]);

        setRoleError(true);
      } finally {
        if (!signal?.aborted) {
        }
      }
    },
    [instance, accounts],
  );

  useEffect(() => {
    if (!accounts.length) {
      return;
    }

    const controller = new AbortController();

    loadRole(controller.signal);

    return () => {
      controller.abort();
    };
  }, [accounts.length, loadRole]);

  /* ---------------- MODULE FILTER ---------------- */
  const modules = useMemo(() => {
    if (loading) return [];

    /* =========================================
     ADMIN
  ========================================= */

    const isAdmin =
      Array.isArray(permissions) &&
      permissions.some((p) => p.resource_code === "ADMIN");

    if (isAdmin) {
      return ALL_MODULES;
    }

    /* =========================================
     NORMAL MODULE FILTER
  ========================================= */

    return ALL_MODULES.filter((module) =>
      module.resources.some((resource) =>
        permissions?.some((p) => p.resource_code === resource),
      ),
    );
  }, [permissions, loading]);

  /* ---------------- RENDER ---------------- */
  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <h1 style={styles.title}>Welcome</h1>
        <p style={styles.subtitle}>Quick access to your portal tools.</p>

        <div style={styles.roleBadge}>
          <span style={{ fontWeight: 600 }}>Roles:</span>

          {role.length > 0 ? (
            role.map((r, i) => (
              <span key={i} style={styles.role}>
                {r}
              </span>
            ))
          ) : (
            <span style={styles.role}>
              {roleError ? "No Role Assigned" : "Loading..."}
            </span>
          )}
        </div>
      </div>

      {/* 🔥 NO ROLE ERROR */}
      {roleError && (
        <div style={styles.errorBox}>
          ❌ You do not have any role assigned.
          <br />
          Please contact administrator.
        </div>
      )}

      {/* LOADING */}
      {loading ? (
        <div style={{ padding: "20px" }}>Loading modules...</div>
      ) : roleError ? null : modules.length === 0 ? ( // don't show modules if no role
        <div style={styles.emptyBox}>⚠️ No accessible modules available.</div>
      ) : (
        <div style={styles.grid}>
          {modules.map((m, i) => {
            const Icon = m.icon;

            return (
              <div
                key={i}
                style={styles.card}
                onClick={() => {
                  /*
    Prevent accidental double navigation
  */

                  if (window.location.pathname !== m.path) {
                    navigate(m.path);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={styles.iconWrapper}>
                  <Icon size={22} />
                </div>

                <div>
                  <h3 style={styles.cardTitle}>{m.title}</h3>
                  <p style={styles.cardDesc}>{m.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const styles = {
  container: {
    padding: "10px",
    background: "#f8fafc",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minHeight: "80vh",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "600",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  subtitle: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "14px",
  },
  roleBadge: {
    marginTop: "12px",
    fontSize: "13px",
    color: "#475569",

    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
  },

  role: {
    background: "#e0f2fe",
    color: "#0369a1",
    padding: "5px 12px",
    borderRadius: "20px",
    fontWeight: "500",

    whiteSpace: "nowrap",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  emptyBox: {
    background: "#fef9c3",
    color: "#92400e",
    padding: "12px",
    borderRadius: "8px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))",
    gap: "18px",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  card: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    padding: "18px",
    borderRadius: "12px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: 0,
    boxSizing: "border-box",
  },
  iconWrapper: {
    background: "#f1f5f9",
    padding: "10px",
    borderRadius: "10px",
    display: "flex",
  },
  cardTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
  },
  cardDesc: {
    margin: "4px 0 0",
    fontSize: "13px",
    color: "#64748b",
  },
};
